import { Resolver, Query, FieldResolver, Root, Args, Arg, Ctx, Directive } from 'type-graphql'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import type ResolverContext from '@things-factory/auth-base'
import { KpiStatistic } from './kpi-statistic.js'
import { KpiStatisticList } from './kpi-statistic-type.js'
import { Kpi, KpiPeriodType } from '../kpi/kpi.js'
import { KpiOrgScope } from '../kpi-org-scope/kpi-org-scope.js'

@Resolver(KpiStatistic)
export class KpiStatisticQuery {
  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiStatistic!, { nullable: true, description: 'To fetch a KpiStatistic' })
  async kpiStatistic(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<KpiStatistic> {
    const { domain } = context.state

    return await getRepository(KpiStatistic).findOne({
      where: { domain: { id: domain.id }, id }
    })
  }

  @Query(returns => KpiStatisticList, { description: 'To fetch multiple KpiStatistics' })
  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  async kpiStatistics(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<KpiStatisticList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      domain,
      params,
      repository: await getRepository(KpiStatistic),
      searchables: ['kpi', 'valueDate'],
      filtersMap: {
        kpi: { columnName: 'id', relationColumn: 'kpi' },
        kpiOrgScope: { columnName: 'id', relationColumn: 'kpiOrgScope' },
        periodType: { columnName: 'periodType' },
        valueDate: { columnName: 'valueDate' },
        scope02: { 
          columnName: 'scope02', 
          relationColumn: 'kpiOrgScope'
        }
      }
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => Kpi)
  async kpi(@Root() kpiStatistic: KpiStatistic): Promise<Kpi> {
    return kpiStatistic.kpiId && (await getRepository(Kpi).findOneBy({ id: kpiStatistic.kpiId }))
  }

  @FieldResolver(type => Domain)
  async domain(@Root() kpiStatistic: KpiStatistic): Promise<Domain> {
    return kpiStatistic.domainId && (await getRepository(Domain).findOneBy({ id: kpiStatistic.domainId }))
  }

  @FieldResolver(type => User)
  async updater(@Root() kpiStatistic: KpiStatistic): Promise<User> {
    return kpiStatistic.updaterId && (await getRepository(User).findOneBy({ id: kpiStatistic.updaterId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() kpiStatistic: KpiStatistic): Promise<User> {
    return kpiStatistic.creatorId && (await getRepository(User).findOneBy({ id: kpiStatistic.creatorId }))
  }

  @FieldResolver(type => KpiOrgScope)
  async kpiOrgScope(@Root() kpiStatistic: KpiStatistic): Promise<KpiOrgScope> {
    return kpiStatistic.kpiOrgScopeId && (await getRepository(KpiOrgScope).findOneBy({ id: kpiStatistic.kpiOrgScopeId }))
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => [KpiStatistic], { 
    description: 'Get regional statistics for dashboard map visualization' 
  })
  async regionalKpiStatistics(
    @Arg('valueDate') valueDate: string,
    @Arg('periodType', type => KpiPeriodType, { defaultValue: KpiPeriodType.MONTH }) periodType: KpiPeriodType,
    @Arg('regions', type => [String], { nullable: true }) regions: string[] | undefined,
    @Ctx() context: ResolverContext
  ): Promise<KpiStatistic[]> {
    const { domain } = context.state
    
    const queryBuilder = getRepository(KpiStatistic)
      .createQueryBuilder('stat')
      .leftJoinAndSelect('stat.kpi', 'kpi')
      .leftJoinAndSelect('stat.kpiOrgScope', 'orgScope')
      .where('stat.domain = :domainId', { domainId: domain.id })
      .andWhere('stat.periodType = :periodType', { periodType })
      .andWhere('stat.valueDate = :valueDate', { valueDate })
      .andWhere('orgScope.scope02 IS NOT NULL') // 지역 정보가 있는 것만
      
    if (regions && regions.length > 0) {
      queryBuilder.andWhere('orgScope.scope02 IN (:...regions)', { regions })
    }
      
    queryBuilder.orderBy('orgScope.scope02', 'ASC')
      .addOrderBy('kpi.name', 'ASC')
    
    return await queryBuilder.getMany()
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => [KpiStatistic], { 
    description: 'Get aggregated statistics by scope02 (regional level) for dashboard' 
  })
  async dashboardRegionalStatistics(
    @Arg('valueDate') valueDate: string,
    @Arg('periodType', type => KpiPeriodType, { defaultValue: KpiPeriodType.MONTH }) periodType: KpiPeriodType,
    @Ctx() context: ResolverContext
  ): Promise<KpiStatistic[]> {
    const { domain } = context.state
    
    // 지역별로 평균을 내서 하나의 통계로 합침
    const query = `
      SELECT 
        'regional-aggregate-' || orgScope.scope02 as id,
        kpi.id as kpiId,
        kpi.name as kpiName,
        orgScope.scope02 as region,
        AVG(stat.mean) as avgMean,
        AVG(stat.median) as avgMedian,
        MIN(stat.minimum) as overallMin,
        MAX(stat.maximum) as overallMax,
        AVG(stat.standardDeviation) as avgStdDev,
        SUM(stat.count) as totalCount,
        COUNT(*) as orgCount
      FROM kpi_statistic stat
      LEFT JOIN kpi ON stat.kpiId = kpi.id
      LEFT JOIN kpi_org_scope orgScope ON stat.kpiOrgScopeId = orgScope.id
      WHERE stat.domainId = ?
        AND stat.periodType = ?
        AND stat.valueDate = ?
        AND orgScope.scope02 IS NOT NULL
      GROUP BY kpi.id, orgScope.scope02
      ORDER BY orgScope.scope02, kpi.name
    `
    
    return await getRepository(KpiStatistic).query(query, [domain.id, periodType, valueDate])
  }
}
